<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import SongRow from './SongRow.vue'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()
const backgroundImage = `url('${asset('img/student-v2/staircase/molly_background.svg')}')`

const props = defineProps<{ context: Student2Context }>()

const characterActive = ref(false)
const showOverlay = ref(false)

const container = useTemplateRef<HTMLElement>('container')
const list = useTemplateRef<HTMLElement>('list')
const overlay = useTemplateRef<HTMLElement>('overlay')
const resizeObserver = ref<ResizeObserver | null>(null)

const songs = computed(() => {
  return (props.context?.songs ?? []).map(s => {
    const currentUnitId = props.context?.smartbook?.last_unit_unlocked_by_progress
    const songKey = s.key
    const hasTail = (props.context?.student?.tails ?? []).some(
      t =>
        t.tail_type === 'control_item' &&
        (typeof t.custom_data?.song === 'string' ? t.custom_data.song : t.custom_data?.song?.key) === songKey
    )

    return {
      name: s.name,
      outline: hasTail || s.unit_id === currentUnitId,
      accepted: s.accepted
    }
  })
})

showOverlay.value = songs.value.every(e => e.accepted)

const relPxVert = (val: number) => (val / 780) * 100
const relPxHoriz = (val: number) => (val / 555) * 100

const resizeList = () => {
  if (!container.value || !list.value) {
    return
  }

  const rect = container.value.getBoundingClientRect()
  const width = window.matchMedia('(orientation: portrait)').matches ? rect.height : rect.width

  const widthFrac = width / 555
  list.value.style.transform = `scale(${widthFrac})`
}

const animatePromise = (
  el: Element,
  kf: Keyframe[] | PropertyIndexedKeyframes,
  opt?: number | KeyframeAnimationOptions
) => {
  return new Promise<void>(resolve => {
    const anim = el.animate(kf, opt)
    anim.onfinish = () => resolve()
  })
}

const animate = async () => {
  if (!overlay.value || !container.value) {
    return
  }

  // x, y - relative coordinates
  // o - opacity
  // r - rotation (deg)
  const itemKeyframes = [
    { x: 130, y: 231, r: 61, o: 0 },
    { x: 130, y: 231, r: 61, o: 1 },
    { x: 130, y: 254, r: 30, o: 1 },
    { x: 151, y: 297, r: 0, o: 1 },
    { x: 130, y: 326, r: -30, o: 1 },
    { x: 82, y: 326, r: -30, o: 1 },
    { x: 42, y: 327, r: -60, o: 1 },
    { x: 37, y: 371, r: -90, o: 1 },
    { x: 37, y: 451, r: -90, o: 1 },
    { x: 18, y: 511, r: -120, o: 1 },
    { x: 18, y: 590, r: -180, o: 1 },
    { x: 18, y: 630, r: -270, o: 1 },
    { x: 98, y: 630, r: -360, o: 1 },
    { x: 119.15, y: 673, r: -360, o: 1 }
  ]

  const item = document.createElement('img')
  item.src = asset('img/student-v2/staircase/molly_item.png')
  item.style.position = 'absolute'
  item.style.transformOrigin = 'center center'
  item.style.zIndex = '3'
  item.style.height = `${relPxVert(230)}%`

  const keyframes = itemKeyframes.map(frame => ({
    top: `${relPxVert(frame.y - 49)}%`,
    left: `${relPxHoriz(frame.x - 60)}%`,
    transform: `rotate(${frame.r}deg)`,
    opacity: frame.o
  }))

  const options: KeyframeAnimationOptions = {
    duration: 6000,
    iterations: 1,
    fill: 'forwards',
    easing: 'ease'
  }

  await animatePromise(overlay.value, [{ opacity: 1 }, { opacity: 0 }], { duration: 200 })
  showOverlay.value = false

  container.value.append(item)
  await animatePromise(item, keyframes, options)
  characterActive.value = true
  await animatePromise(item, [{ opacity: 1 }, { opacity: 0 }], { duration: 200 })
  item.remove()
}

const setActive = () => {
  characterActive.value = true
}

onMounted(() => {
  if (!container.value) {
    return
  }

  resizeObserver.value = new ResizeObserver(resizeList)
  resizeObserver.value.observe(container.value)
  resizeList()
})

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect()
})

defineExpose({ setActive, animate })
</script>

<template>
  <div ref="container" class="student-v2-molly">
    <div v-show="showOverlay" ref="overlay" class="student-v2-molly__overlay">
      <v-button mode="primary filled" @click="animate">
        <v-icon class="mr-1">mdi-party-popper</v-icon>
        Помочь Молли
      </v-button>
    </div>

    <img
      class="student-v2-molly__character"
      :class="{ 'student-v2-molly__character_visible': characterActive }"
      :src="asset('img/student-v2/staircase/molly_character_active.svg')"
    />
    <img
      class="student-v2-molly__character"
      :class="{ 'student-v2-molly__character_visible': !characterActive }"
      :src="asset('img/student-v2/staircase/molly_character_idle.svg')"
    />

    <ul ref="list" class="student-v2-molly__songs-list">
      <ul style="gap: 4px">
        <li><SongRow v-if="songs[15]" :song="songs[15]" /></li>
        <li><SongRow v-if="songs[14]" :song="songs[14]" /></li>
        <li><SongRow v-if="songs[13]" :song="songs[13]" /></li>
      </ul>
      <ul style="gap: 4px; padding-left: 16px">
        <li><SongRow v-if="songs[12]" :song="songs[12]" /></li>
        <li><SongRow v-if="songs[11]" :song="songs[11]" /></li>
      </ul>
      <ul style="gap: 6px; padding-left: 32px">
        <li><SongRow v-if="songs[10]" :song="songs[10]" /></li>
        <li><SongRow v-if="songs[9]" :song="songs[9]" /></li>
        <li><SongRow v-if="songs[8]" :song="songs[8]" /></li>
        <li><SongRow v-if="songs[7]" :song="songs[7]" /></li>
      </ul>
      <ul style="gap: 10px; padding-left: 32px">
        <li><SongRow v-if="songs[6]" :song="songs[6]" /></li>
        <li><SongRow v-if="songs[5]" :song="songs[5]" /></li>
        <li><SongRow v-if="songs[4]" :song="songs[4]" /></li>
        <li><SongRow v-if="songs[3]" :song="songs[3]" /></li>
      </ul>
      <ul style="gap: 12px; padding-left: 20px">
        <li><SongRow v-if="songs[2]" :song="songs[2]" /></li>
        <li><SongRow v-if="songs[1]" :song="songs[1]" /></li>
        <li><SongRow v-if="songs[0]" :song="songs[0]" /></li>
      </ul>
    </ul>
  </div>
</template>

<style lang="scss">
@function relPxVert($val) {
  @return calc((#{$val} / 780) * 100%);
}

@function relPxHoriz($val) {
  @return calc((#{$val} / 555) * 100%);
}

.student-v2-molly {
  aspect-ratio: 555 / 780;
  max-width: min(555px, 90%);

  position: relative;
  overflow: hidden;
  background-image: v-bind(backgroundImage);
  background-size: cover;

  &__character {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    transition: opacity 0.2s;
    transition-delay: 0.2s;
    opacity: 0;
    z-index: 0;

    &_visible {
      transition-delay: 0s;
      opacity: 1;
      z-index: 1;
    }
  }

  &__songs-list {
    position: absolute;
    z-index: 2;
    top: relPxVert(195);
    left: relPxHoriz(246);

    transform-origin: left top;

    display: flex;
    flex-direction: column;
    gap: 10px;

    & > ul {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    li {
      height: 24px;
      font-size: 12px;
      line-height: 16px;

      background: white;
      box-shadow: -1px 1px 4px rgba(58, 70, 93, 0.12);
      border-radius: 16px;
      padding: 0 8px;
    }
  }

  &__overlay {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 5;
  }
}
</style>
